export const agentKeys = {
  all: ['agents'] as const,
  list: () => [...agentKeys.all, 'list'] as const,
  enabled: () => [...agentKeys.all, 'enabled'] as const,
} as const;

export const agentManagementKeys = {
  all: ['agent-management'] as const,
  list: () => [...agentManagementKeys.all, 'list'] as const,
} as const;

export const cronKeys = {
  all: ['crons'] as const,
  list: () => [...cronKeys.all, 'list'] as const,
  /** The Schedule page's searched list. `q` is normalized here once and
   *  the hook sends this same value, so an entry can never describe a
   *  different query than it holds; a blank term IS the plain list. */
  search: (q: string) => {
    const term = q.trim();
    return term ? ([...cronKeys.list(), { q: term }] as const) : cronKeys.list();
  },
  detail: (id: string) => [...cronKeys.all, 'detail', id] as const,
  /** FRONT-2439: the system reflection jobs, as the Reflection page shows them. */
  reflection: () => [...cronKeys.all, 'reflection'] as const,
  runs: (id: string) => [...cronKeys.all, 'runs', id] as const,
  latestRun: (id: string) => [...cronKeys.all, 'runs', id, 'latest'] as const,
  authStatus: () => [...cronKeys.all, 'auth-status'] as const,
  /** FRONT-2170: the workspace default model a job without its own model runs on. */
  defaultModel: () => [...cronKeys.all, 'default-model'] as const,
} as const;

export const workflowScheduleKeys = {
  all: ['workflow-schedules'] as const,
  list: () => [...workflowScheduleKeys.all, 'list'] as const,
} as const;

export const presetKeys = {
  all: ['presets'] as const,
  list: () => [...presetKeys.all, 'list'] as const,
} as const;

/**
 * FRONT-1907: the key the workflow editor library holds its unattended-approval
 * answer under. A hand copy — the library does not export it — so that Eddy's
 * own writes can reach a cache entry the library owns.
 * `use-workflow-auto-approval.test.ts` reads it back out of the shipped bundle
 * rather than trusting this line.
 */
export const workflowUnattendedApprovalKeys = {
  detail: (workflowId: string) => ['workflow-unattended-approval', workflowId] as const,
} as const;

export const skillKeys = {
  all: ['skills'] as const,
  list: () => [...skillKeys.all, 'list'] as const,
  detail: (slug: string) => [...skillKeys.all, 'detail', slug] as const,
  /** FRONT-1694 / ADR-0049: a skill's bundled scripts/references/assets. */
  files: (slug: string) => [...skillKeys.all, 'files', slug] as const,
  /** FRONT-2364 review round 1, F11: the caller-scoped agent roster the skill
   *  editor's Agents picker reads from (GET /api/skills/agents). */
  agents: () => [...skillKeys.all, 'agents'] as const,
  /** FRONT-2364 fix round 2: the exact tool universe a skill run can call
   *  (GET /api/skills/tools) — the editor's Tools picker/chips and the
   *  draft prompt's own candidate list share this one server answer. */
  tools: () => [...skillKeys.all, 'tools'] as const,
} as const;

// FRONT-1349: the built-in quick-capture slash commands (/note, /task,
// /memory) — a separate, tenant-unconfigurable list from skillKeys above.
export const chatCommandKeys = {
  all: ['chat-commands'] as const,
  list: () => [...chatCommandKeys.all, 'list'] as const,
} as const;

export const toolKeys = {
  all: ['tools'] as const,
  list: () => [...toolKeys.all, 'list'] as const,
  native: () => [...toolKeys.all, 'native'] as const,
  custom: () => [...toolKeys.all, 'custom'] as const,
  customDetail: (slug: string) => [...toolKeys.all, 'custom', slug] as const,
} as const;

export const memoryKeys = {
  all: ['memory'] as const,
  general: () => [...memoryKeys.all, 'general'] as const,
  agent: (agentId: string) => [...memoryKeys.all, 'agent', agentId] as const,
} as const;

export const graphKeys = {
  all: ['graph'] as const,
  // FRONT-892: `archived` is normalized to a bool (default false) so the
  // active vs archived Work-board lists cache under distinct keys instead of
  // silently sharing the plain entities(type, search) key.
  entities: (type?: string, search?: string, archived?: boolean) =>
    [...graphKeys.all, 'entities', type, search, archived ?? false] as const,
  entity: (id: string) => [...graphKeys.all, 'entity', id] as const,
  search: (query: string) => [...graphKeys.all, 'search', query] as const,
  // FRONT-1256: the command palette's Work section — a project-only,
  // 5-result search. Kept under its own key (not `search`) since the request
  // shape (types + limit) and cached result differ from the unfiltered
  // Memory-page search `search()` backs.
  workSearch: (query: string) => [...graphKeys.all, 'work-search', query] as const,
  // FRONT-876: the palette's Memory section — the same shape for the
  // person/company/event/preference kinds. Its own key for the same reason.
  memorySearch: (query: string) => [...graphKeys.all, 'memory-search', query] as const,
  stats: () => [...graphKeys.all, 'stats'] as const,
  health: () => [...graphKeys.all, 'health'] as const,
  activity: (limit?: number) => [...graphKeys.all, 'activity', limit] as const,
  subgraph: (entityId: string) => [...graphKeys.all, 'subgraph', entityId] as const,
  conflicts: () => [...graphKeys.all, 'conflicts'] as const,
  conflictCount: () => [...graphKeys.all, 'conflict-count'] as const,
  trash: () => [...graphKeys.all, 'trash'] as const,
  trashCount: () => [...graphKeys.all, 'trash-count'] as const,
} as const;

// FRONT-893: manual tasks inside a Work-board container (a project).
// Keyed under the container id so each row's disclosure caches independently
// and a single container's list can be invalidated without touching others.
export const workTaskKeys = {
  all: ['work-tasks'] as const,
  byContainer: (containerId: string) => [...workTaskKeys.all, 'container', containerId] as const,
} as const;

// FRONT-2423: a task's comment thread — keyed under the task id (not the
// container) so one task's comments invalidate/refetch independently of its
// siblings' (CLAUDE.md FRONT-1501: the key must contain every input that
// changes the request).
export const taskCommentKeys = {
  all: ['work-task-comments'] as const,
  byTask: (taskId: string) => [...taskCommentKeys.all, 'task', taskId] as const,
} as const;

// FRONT-1305: a Work container's ongoing notes — the idea pool distinct
// from its Tasks. Keyed under the container id, same shape as workTaskKeys,
// so each container's list caches and invalidates independently.
export const workNoteKeys = {
  all: ['work-notes'] as const,
  byContainer: (containerId: string) => [...workNoteKeys.all, 'container', containerId] as const,
} as const;

// FRONT-2583: a project's saved detail-page box layout
// (`GET /api/work/containers/:id/layout`). `byContainer` folds the container
// id into the key (FRONT-1501 key rule) so two projects never share an entry;
// `all` is the invalidation prefix.
export const workContainerLayoutKeys = {
  all: ['work-container-layout'] as const,
  byContainer: (containerId: string) => [...workContainerLayoutKeys.all, 'container', containerId] as const,
  // MUTATION key (not a query key): shared by save and reset so overlapping
  // writes for the same container can see each other via `isMutating`.
  mutation: (containerId: string) => [...workContainerLayoutKeys.all, 'mutate', containerId] as const,
} as const;

// FRONT-1085: a Work container's attachments — pointers to Files-screen
// items (an upload or a saved document). Keyed under the container id, same
// shape as workTaskKeys/workNoteKeys, so each container's attachment list
// caches and invalidates independently.
export const workFileKeys = {
  all: ['work-files'] as const,
  byContainer: (containerId: string) => [...workFileKeys.all, 'container', containerId] as const,
} as const;

// FRONT-2560: the review state of one approvable item in a Project
// (`GET /api/work/:projectId/items/:kind/:itemId/review`, FRONT-1950). Keyed by
// kind so any kind with a review adapter shares the same cache shape.
export const itemReviewKeys = {
  all: ['item-review'] as const,
  one: (projectId: string, kind: string, itemId: string) => [...itemReviewKeys.all, projectId, kind, itemId] as const,
  /** Every key of one kind in one Project (single items and batches): what a decision invalidates. */
  kind: (projectId: string, kind: string) => [...itemReviewKeys.all, projectId, kind] as const,
  /** FRONT-2568: several items' states in one request, for a Files list. */
  batch: (projectId: string, kind: string, itemIds: readonly string[]) =>
    [...itemReviewKeys.all, projectId, kind, 'batch', [...itemIds].sort().join(',')] as const,
} as const;

// FRONT-2558: company website connections (Settings → Connections → Websites) and
// where one site Project publishes. Keys carry ids only — never a credential.
export const websiteConnectionKeys = {
  all: ['website-connections'] as const,
  providers: () => [...websiteConnectionKeys.all, 'providers'] as const,
  list: () => [...websiteConnectionKeys.all, 'list'] as const,
  publishTargets: (projectId: string) => [...websiteConnectionKeys.all, 'publish-targets', projectId] as const,
} as const;

// FRONT-1696: a Work container's standalone custom instructions
// (`GET /api/work/containers/:id/instructions`) — keyed directly under the
// container id (no extra 'container' segment like `workFileKeys.byContainer`
// — there's exactly one instructions value per container, not a list, so
// there's no sibling collection to disambiguate from).
export const workContainerInstructionsKeys = {
  all: ['work-container-instructions'] as const,
  byContainer: (containerId: string) => [...workContainerInstructionsKeys.all, containerId] as const,
} as const;

// FRONT-1003: the Work board's aggregate read (`GET /api/work/board`) — one
// row per project container with task counts, next open task, people, and a
// context snippet already computed server-side. A single unfiltered fetch
// (no pagination); the board page filters by view client-side, see
// `routes/work.tsx`.
export const workBoardKeys = {
  all: ['work-board'] as const,
} as const;

// FRONT-2261: a single container's true last-activity value
// (`GET /api/work/containers/:id/activity`) — the same computation the
// board card reads via `WorkBoardItem.lastActivityAt`. Nested UNDER
// `workBoardKeys.all` (not a sibling top-level key) so every mutation that
// already invalidates the board (`qc.invalidateQueries({queryKey:
// workBoardKeys.all})`) also refetches this — TanStack matches query keys
// by prefix, and `['work-board']` is a prefix of `['work-board',
// 'activity', containerId]`. `byContainer` folds the container id into the
// key itself (FRONT-1501 key rule) so two different containers' activity
// never collide in the cache.
export const workContainerActivityKeys = {
  all: [...workBoardKeys.all, 'activity'] as const,
  byContainer: (containerId: string) => [...workContainerActivityKeys.all, containerId] as const,
} as const;

// FRONT-2378: a project's attached automations (recurring cron jobs and
// platform workflows) — `GET /api/work/containers/:id/automations` and its
// `/attachable` sibling. `lists()` is the invalidation target every
// attach/detach mutation hits; `list(containerId)` folds the container id
// into the key itself (FRONT-1501 key rule — a fixed key would leave every
// project reading the FIRST project's cached automations) so two different
// projects' lists never collide. `attachable(containerId)` is a distinct
// endpoint (different shape: `{crons, workflows}`, not `{automations}`), kept
// as its own key rather than a variant of `list` so invalidating one never
// silently refetches the other under a shared prefix.
export const projectAutomationKeys = {
  all: ['project-automations'] as const,
  lists: () => [...projectAutomationKeys.all, 'list'] as const,
  list: (containerId: string) => [...projectAutomationKeys.lists(), containerId] as const,
  attachable: (containerId: string) => [...projectAutomationKeys.all, 'attachable', containerId] as const,
} as const;

export const approvalKeys = {
  all: ['approvals'] as const,
  list: () => [...approvalKeys.all, 'list'] as const,
  /** FRONT-2307: one late approval's run status (`useApprovalExecution`). */
  execution: (approvalId: string) => [...approvalKeys.all, 'execution', approvalId] as const,
} as const;

/**
 * FRONT-1488: the sidebar's attention badges.
 *
 * Its own key so the queues it counts can invalidate it. The badge is derived
 * from reflect proposals, graph conflicts and the out-of-date knowledge list, so ANY code that invalidates
 * `reflectKeys.all`, the graph-conflict keys or `knowledgeReviewKeys.lists()` must invalidate this too, or the
 * badge keeps advertising work the user has already done until the 60s poll
 * catches up. `attention-invalidation.test.ts` fails the build if a site forgets.
 */
export const attentionKeys = {
  all: ['attention'] as const,
} as const;

export const reflectKeys = {
  all: ['reflect'] as const,
  proposals: (status?: string) => [...reflectKeys.all, 'proposals', status] as const,
  runs: () => [...reflectKeys.all, 'runs'] as const,
  // FRONT-2498: nested under `all`, so accepting or rejecting anywhere refreshes an open run too.
  runProposals: (runId: number) => [...reflectKeys.all, 'run-proposals', runId] as const,
  mode: () => [...reflectKeys.all, 'mode'] as const,
  enabled: () => [...reflectKeys.all, 'enabled'] as const,
  prompt: () => [...reflectKeys.all, 'prompt'] as const,
} as const;

// FRONT-894 ("commitment catcher"): task_create reflect proposals shown on
// the Work board's "Needs your OK" strip. Same underlying reflect_proposals
// data as reflectKeys, but a narrower query (kind=task_create) consumed by a
// different surface (Work, not the Memory reflections inbox) — kept in its
// own namespace so invalidating one doesn't over-invalidate the other.
export const commitmentKeys = {
  all: ['commitments'] as const,
  list: () => [...commitmentKeys.all, 'list'] as const,
} as const;

export const planKeys = {
  all: ['plans'] as const,
  bySession: (sessionId: string) => [...planKeys.all, sessionId] as const,
} as const;

export const questionKeys = {
  all: ['questions'] as const,
  bySession: (sessionId: string) => [...questionKeys.all, sessionId] as const,
  /**
   * FRONT-1685: a card's live-status check must ask about its OWN batch,
   * not "whatever's current" — two different batches' cards on screen at
   * once (an older one plus a newer follow-up) would otherwise share one
   * cached response under `bySession` and one of them would read the
   * other's status. Nests under `bySession` so invalidating that broader
   * key (a `questions-changed` event) still invalidates every open card's
   * specific entry too.
   */
  byBatch: (sessionId: string, batchId: number) =>
    [...questionKeys.bySession(sessionId), batchId] as const,
} as const;

export const saveProposalKeys = {
  all: ['save-proposals'] as const,
  bySession: (sessionId: string) => [...saveProposalKeys.all, sessionId] as const,
} as const;

export const researchKeys = {
  all: ['research'] as const,
  bySession: (sessionId: string) => [...researchKeys.all, 'session', sessionId] as const,
  byRun: (skillRunId: string) => [...researchKeys.all, 'run', skillRunId] as const,
  citations: (runId: number) => [...researchKeys.all, 'citations', runId] as const,
  // FRONT-1500: a run's durable timeline (the chat work-log's data source).
  timeline: (runId: number) => [...researchKeys.all, 'timeline', runId] as const,
} as const;

export const backgroundChatKeys = {
  all: ['background-chat'] as const,
  list: () => [...backgroundChatKeys.all, 'list'] as const,
  bySession: (sessionId: string) => [...backgroundChatKeys.all, 'session', sessionId] as const,
  byRun: (runId: string) => [...backgroundChatKeys.all, 'run', runId] as const,
} as const;

// FRONT-3211: a chat's tasks (slow delegations that left the turn) and their
// agents' live steps, keyed by the chat they belong to.
export const chatTaskKeys = {
  all: ['chat-tasks'] as const,
  list: (sessionId: string) => [...chatTaskKeys.all, 'list', sessionId] as const,
  progress: (sessionId: string) => [...chatTaskKeys.all, 'progress', sessionId] as const,
} as const;

// FRONT-2991: a voice call's EDDY jobs, keyed by the chat the call runs in.
export const liveCallJobKeys = {
  all: ['live-call-jobs'] as const,
  byChat: (sessionId: string) => [...liveCallJobKeys.all, 'chat', sessionId] as const,
} as const;

// FRONT-661: the chat-status spine. `list(ids)` keys by the sorted session set
// so the launcher (specific chats) and the indicator (all active) cache apart.
export const chatStatusKeys = {
  all: ['chat-status'] as const,
  list: (sessionIds: string[]) =>
    [...chatStatusKeys.all, 'list', [...sessionIds].sort().join(',')] as const,
} as const;

/**
 * FRONT-1717: does one cached chat-status query concern `sessionId`?
 *
 * `background-chat-changed` fires at both ends of every tracked turn, and
 * used to invalidate `chatStatusKeys.all` — so one chat's turn starting
 * refetched the status of every OTHER chat surface the tab had mounted
 * (the reported "as if other conversations are being loaded", FRONT-1815).
 *
 * There is no narrower KEY to invalidate instead: `list()` keys by the
 * whole sorted session SET, so chat A's dot and chat B's dot genuinely
 * share one cache entry whenever they are rendered by the same surface.
 * Scoping therefore has to be a predicate over the cached keys rather than
 * a key prefix. Lives here, next to the factory whose shape it decodes, so
 * the encoder and the decoder cannot drift apart.
 *
 * The set-membership queries (the launcher pills, the inbox modal's single
 * id, files.tsx's per-file set, pwa-shell's set) are skipped when the
 * event's session is not among their ids. The default-set query — `list([])`,
 * the top-left indicator, where the SERVER picks "the user's active chats" —
 * always matches: its membership is not knowable client-side, and a chat
 * whose dot must light up is exactly the kind of chat that set contains.
 * That one refetch is the residual, and it is required for the dots.
 */
export function chatStatusQueryConcerns(
  queryKey: readonly unknown[],
  sessionId: string,
): boolean {
  if (queryKey[0] !== chatStatusKeys.all[0] || queryKey[1] !== 'list') return false;
  const ids = queryKey[2];
  // Unknown third segment — a key shape this helper does not recognise.
  // Refetch rather than silently skip (same fail-open direction the
  // caller uses for a payload with no session id at all).
  if (typeof ids !== 'string') return true;
  // The server-default active set: membership unknown here.
  if (ids === '') return true;
  return ids.split(',').includes(sessionId);
}

export const masterKeys = {
  all: ['master'] as const,
  status: () => [...masterKeys.all, 'status'] as const,
} as const;

export const chatKeys = {
  all: ['chat'] as const,
  history: (sessionId: string, branchId?: string) =>
    [...chatKeys.all, 'history', sessionId, branchId ?? 'main'] as const,
  branches: (sessionId: string) => [...chatKeys.all, 'branches', sessionId] as const,
  // FRONT-518 — chat threads (the "Keep the value" overlay). `threadsRoot` is
  // the invalidation prefix; `threads(state, limit)` is the per-query key.
  threadsRoot: () => [...chatKeys.all, 'threads'] as const,
  threads: (state?: string, limit?: number) =>
    [...chatKeys.all, 'threads', state ?? 'all', limit ?? 0] as const,
  // One thread's lifecycle row (state/resolution), keyed by session id. Nested
  // under the `threads` prefix so a `threadsRoot()` invalidation refreshes it.
  thread: (sessionId: string) => [...chatKeys.all, 'threads', 'one', sessionId] as const,
  // FRONT-527 — the Chats section. `search` backs the Open-tab full-text box;
  // `backlinks` is the filename→source-chat map for the Files "From chat" badge.
  search: (query: string) => [...chatKeys.all, 'threads', 'search', query] as const,
  // FRONT-2433: the Chats page timeline — one paginated read per filter and
  // sort. Nested under `threads` so every chat mutation's `threadsRoot()`
  // invalidation refreshes it.
  timeline: (filter: string, sort: string) => [...chatKeys.all, 'threads', 'timeline', filter, sort] as const,
  backlinks: () => [...chatKeys.all, 'threads', 'backlinks'] as const,
  // FRONT-1738 — the Chats panel on a Work container's detail page
  // (`GET /api/chats?containerId=…`). Nested under the same `threads`
  // segment as `thread`/`search`/`backlinks` above so a plain
  // `threadsRoot()` invalidation (already fired by every mutation that can
  // move a chat in or out of a container) reaches this list too, with no
  // caller needing to know the container-scoped key exists. `states`/`limit`
  // fold into the key (FRONT-1501) so two different filters on the same
  // container — or the same filter on two different containers — never
  // collide in the cache.
  //
  // Review fix: this used to also export a `byContainerRoot()` narrower
  // invalidation prefix, on the claim that some caller wants to refresh
  // every container's chat list WITHOUT also refetching the unscoped
  // Open/Kept/Recycle-bin lists `threadsRoot()` alone would touch. No such
  // caller ever existed — `useSetChatScope` (`use-chat-mutations.ts`), the
  // only site that ever invalidated it, ALSO invalidates `threadsRoot()` in
  // the same call, which already covers `byContainer` by prefix. Removed.
  byContainer: (containerId: string, states?: string, limit?: number) =>
    [...chatKeys.all, 'threads', 'by-container', containerId, states ?? 'active,archived', limit ?? 100] as const,
  // FRONT-1191 — the Work container (a project) a chat is about
  // (`GET /api/chat/sessions/:sessionId/scope`), keyed by session id so a
  // reopened chat's marker chip caches independently per session.
  scope: (sessionId: string) => [...chatKeys.all, 'scope', sessionId] as const,
} as const;

export const usageKeys = {
  all: ['usage'] as const,
  stats: (days?: number) => [...usageKeys.all, 'stats', days] as const,
  // FRONT-1463: the signed-in person's own usage panel in Settings
  // (`GET /api/usage/me`) — a distinct sub-key from `stats` above. `stats`
  // is ALSO per-user, not org-wide (`getUsageStats` filters
  // `eq(llmTraces.userId, userId)`), and backs the Audit page's own usage
  // card — so the app now has two personal usage read surfaces sharing the
  // `usage` key root: `stats`/`/api/usage` (Audit page) and `me`/
  // `/api/usage/me` (Settings). They never share a cache entry, and a
  // day-count change (7 vs 30) is its own key so switching the window
  // always triggers a real refetch instead of reusing stale data.
  me: (days: number) => [...usageKeys.all, 'me', days] as const,
  // FRONT-1499: the day drill-down opened by clicking a trend bar
  // (`GET /api/usage/me/day?day=...`) — its own sub-key (not nested under
  // `me`) since it's a different endpoint entirely, keyed by the day string
  // so each day's detail caches and can be invalidated independently.
  // `null` (no day selected, the modal closed) keys distinctly too, though
  // the hook never fetches for it (`enabled: !!day`).
  meDay: (day: string | null) => [...usageKeys.all, 'me-day', day] as const,
  // FRONT-1612: the person's own coding bill (`GET /api/usage/me/coding`),
  // keyed by window like `me` so a 7/30 switch is a real refetch.
  meCoding: (days: number) => [...usageKeys.all, 'me-coding', days] as const,
  // FRONT-1612: the person's own spend by activity (`GET /api/usage/me/activity`).
  meActivity: (days: number) => [...usageKeys.all, 'me-activity', days] as const,
} as const;

// FRONT-1462: an app owner's per-app usage read (`GET /api/apps/:slug/usage`)
// — the Apps admin list's "Analytics" screen. Keyed under both the slug and
// the window so switching either app or window is a genuinely different
// cache entry, same reasoning as `usageKeys.me`'s day-count key above.
export const appUsageKeys = {
  all: ['app-usage'] as const,
  // `window` is 7 | 30 | 'all' (`AppUsageWindow` in use-app-usage.ts) — kept
  // as `number | 'all'` here rather than importing that type, so this file
  // stays free of hook imports.
  detail: (slug: string, window: number | 'all') => [...appUsageKeys.all, slug, window] as const,
} as const;

// FRONT-2937: an app's editors, which only its owner reads.
export const appEditorKeys = {
  all: ['app-editors'] as const,
  list: (slug: string) => [...appEditorKeys.all, slug] as const,
} as const;

// FRONT-2940: an app's drafts, which its owner and editors read; the slug is the request's only input.
export const appDraftKeys = {
  all: ['app-drafts'] as const,
  lists: () => [...appDraftKeys.all, 'list'] as const,
  list: (slug: string) => [...appDraftKeys.lists(), slug] as const,
} as const;

/** FRONT-3007: an app's data-storage figures (owner-only). */
export const appStorageKeys = {
  all: ['app-storage'] as const,
  detail: (slug: string) => [...appStorageKeys.all, slug] as const,
} as const;

/** FRONT-2943: an app's Activity; a code change's changes sit beside the list, so re-reading the list leaves them be. */
export const appActivityKeys = {
  all: ['app-activity'] as const,
  lists: () => [...appActivityKeys.all, 'list'] as const,
  list: (slug: string) => [...appActivityKeys.lists(), slug] as const,
  changes: (slug: string, eventId: number) => [...appActivityKeys.all, 'changes', slug, eventId] as const,
} as const;

export const budgetKeys = {
  all: ['budget'] as const,
  me: () => [...budgetKeys.all, 'me'] as const,
  org: () => [...budgetKeys.all, 'org'] as const,
} as const;

export const llmKeys = {
  all: ['llm'] as const,
  models: () => [...llmKeys.all, 'models'] as const,
  settings: () => [...llmKeys.all, 'settings'] as const,
  /** FRONT-1702: per-tenant extra model grants (super-admin only). */
  extraModels: () => [...llmKeys.all, 'extra-models'] as const,
  /**
   * The FULL proxy catalogue (?all=1). Fetched by the grant picker
   * (extra-models-block.tsx) and, since FRONT-2260 fix round 1 (F4), by
   * ModelCombobox's "not available" membership check — it decides that
   * label against the runtime's real accept/reject rule (the full
   * catalogue), never the workspace-filtered offering the dropdown itself
   * lists.
   *
   * Deliberately NOT nested under `models()`: TanStack invalidates by key
   * PREFIX, so `invalidateQueries({ queryKey: llmKeys.models() })` after a
   * grant save would also refetch this much larger, unrelated query.
   */
  allModels: () => [...llmKeys.all, 'catalogue-all'] as const,
} as const;

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (status?: 'pending' | 'resolved' | 'dismissed' | 'all') =>
    [...notificationKeys.all, 'list', status ?? 'all'] as const,
  count: () => [...notificationKeys.all, 'count'] as const,
} as const;

export const feedKeys = {
  all: ['feed'] as const,
  list: (days?: number) => [...feedKeys.all, 'list', days] as const,
  // FRONT-594 — the inbox recycle bin (trashed cron-runs + documents).
  trash: () => [...feedKeys.all, 'trash'] as const,
} as const;

// FRONT-3227 — the tabbed Inbox. One namespace so a single `all` invalidation
// refreshes every tab; `new` folds its days + limit into the key (a different
// window is a different request) and the other tabs take no input.
export const inboxKeys = {
  all: ['inbox'] as const,
  new: (days: number, limit: number) => [...inboxKeys.all, 'new', days, limit] as const,
  todo: () => [...inboxKeys.all, 'todo'] as const,
  review: () => [...inboxKeys.all, 'review'] as const,
  // Deliberately NESTED under review(): invalidating review() refreshes the
  // top bar's count too, so a Review change can never leave the badge stale.
  reviewCount: () => [...inboxKeys.review(), 'count'] as const,
  remember: () => [...inboxKeys.all, 'remember'] as const,
  done: () => [...inboxKeys.all, 'done'] as const,
  // The compact Done markers New hides by — a separate request from the
  // capped Done list, so it has its own key (and is invalidated beside done()).
  markers: () => [...inboxKeys.all, 'markers'] as const,
} as const;

// FRONT-1090 Part B — the inbox feed's drag-and-drop section order
// (`GET`/`PUT /api/feed/section-order`). A single per-user value, not
// parameterized, so one key covers it.
export const feedSectionOrderKeys = {
  all: ['feed-section-order'] as const,
} as const;

// FRONT-2267 — this person's own UI switches (today: whether tooltips show).
// One key, read once at app boot by the app-root provider that feeds the
// tooltip gate, and written from the Building Session's "…" menu — the only
// writer there is. An earlier draft put the switch in Settings and this
// comment still said so after it moved, which sends a reader hunting for a
// control that does not exist.
export const uiPreferencesKeys = {
  all: ['ui-preferences'] as const,
} as const;

// FRONT-857 — the caller's onboarding-identity read (whether the wizard's
// identity step is done, plus the preferred name it collected). Invalidated
// by the onboarding identity-save mutation AND by any other write to the
// caller's own graph self-entity (e.g. a Memory-page rename), since both
// change the same underlying displayName.
export const meKeys = {
  onboarded: () => ['me', 'onboarded'] as const,
} as const;

// FRONT-907 — day-one activation state (seeded onboarding chat + starter-cron
// proposal), per user.
export const dayOneKeys = {
  all: ['day-one'] as const,
  state: () => [...dayOneKeys.all, 'state'] as const,
} as const;

// FRONT-776: named collections of uploads/saved documents on the Files
// screen, whose complete content is loaded into a chat scoped to them.
// `list()` is the paginated grid (ADR-0023); `detail(folderId)` is one
// folder's members — kept apart so renaming/attaching/detaching only
// invalidates the one folder's own cache entry, not the whole grid (which
// still needs its own invalidation too, for the `· N files` chip).
export const folderKeys = {
  all: ['folders'] as const,
  list: () => [...folderKeys.all, 'list'] as const,
  detail: (folderId: string) => [...folderKeys.all, 'detail', folderId] as const,
  // FRONT-776 desktop-drag decision: the whole (source, ref) → folder
  // mapping (`GET /api/folders/membership`) — what the Files tab joins
  // against its own unified file list to show only what's unfiled. A single
  // per-user value, not parameterized, so one key covers it. Invalidated by
  // every mutation that changes WHERE a file is filed: attach/move (both
  // the per-folder picker and the bulk "Move to folder…" action) and detach.
  membership: () => [...folderKeys.all, 'membership'] as const,
} as const;

export const codingKeys = {
  all: ['coding'] as const,
  list: () => [...codingKeys.all, 'list'] as const,
  detail: (id: string) => [...codingKeys.all, 'detail', id] as const,
  /** FRONT-1787: one session's CODE-CONTEXT.md. Keyed by session id so the
   *  transcript note and the editor dialog share one cached read, and a save
   *  invalidates exactly the session it changed. */
  context: (sessionId: string) => [...codingKeys.all, 'context', sessionId] as const,
  /** FRONT-2220: the Connections servers a session can attach, and which are. */
  mcpServers: (sessionId: string) => [...codingKeys.all, 'mcp-servers', sessionId] as const,
  /** FRONT-2382: one session's "Design direction" brief. Same shape as
   *  `context` above — keyed by session id so the Project-context dialog's
   *  editor gets one cached read per session, and a save invalidates exactly
   *  the session it changed. (The New-project form never reads through this
   *  key — its own draft is local state, sent once on create.) */
  design: (sessionId: string) => [...codingKeys.all, 'design', sessionId] as const,
  /** FRONT-2935: whether the app has changes this session's draft lacks. */
  draftUpdate: (sessionId: string) => [...codingKeys.all, 'draft-update', sessionId] as const,
  /** FRONT-2962: what a session has cost. `endedRuns` is in the key so a run
   *  ending is a new read — the charge or estimate for it changes the figure. */
  cost: (sessionId: string, endedRuns: number) => [...codingKeys.all, 'cost', sessionId, endedRuns] as const,
} as const;

// FRONT-1501: the Sketches gallery — living HTML documents `sketch_show`
// creates in chat (FRONT-1338). A single paginated grid, no per-item detail
// query (rename/delete only ever invalidate the list).
// FRONT-1602: the Slides gallery — decks `deck_publish` creates in chat (apps
// rows with kind = "deck"). Same key discipline as sketchKeys below: the
// search term is folded into the specific key, `lists()` is the invalidation
// prefix.
/**
 * FRONT-2202: the Slides and Sketches galleries gained the Apps list's owner
 * tabs and sort control — and, unlike the Apps list, BOTH change the request
 * rather than re-slicing an already-fetched array (these two endpoints page).
 * So both are folded into the key, for exactly the reason the search term
 * already was: TanStack refetches when the KEY changes, not when the options
 * object does, so a control missing from the key renders, debounces, reads
 * correctly and does nothing at all.
 */
export interface GalleryListKeyInput {
  query?: string;
  owner?: string;
  sort?: string;
}

/** The three controls in one fixed order, with each absent value normalized
 *  ONCE to the same default the server applies — so an untouched control and
 *  an explicitly-default one can never open two cache entries for one list. */
function galleryListKeyParts(input?: GalleryListKeyInput) {
  return [input?.query ?? '', input?.owner ?? 'all', input?.sort ?? 'updated'] as const;
}

export const deckKeys = {
  all: ['decks'] as const,
  lists: () => [...deckKeys.all, 'list'] as const,
  list: (input?: GalleryListKeyInput) => [...deckKeys.lists(), ...galleryListKeyParts(input)] as const,
} as const;

/**
 * FRONT-2142: the command palette's one read across every kind of apps row
 * (apps, decks, sketches). Distinct from `deckKeys`/`sketchKeys` because it is
 * a different endpoint with a different, narrower shape — not a second key over
 * theirs, which is the two-hooks-one-endpoint mistake FRONT-1501 warns about.
 *
 * BOTH inputs are folded into the specific key. `kinds` matters as much as the
 * query does: the palette varies it by feature flag, so two different kind sets
 * are two different requests and must never share a cache entry. `lists()`
 * stays the invalidation prefix.
 */
/**
 * FRONT-2667: colleague suggestions for the named-share field. The query is
 * folded into the key (FRONT-1501), normalized once by the hook.
 */
export const peopleSearchKeys = {
  all: ['people-search'] as const,
  search: (query: string) => [...peopleSearchKeys.all, query] as const,
} as const;

/** FRONT-659: who a resource is shared with (`/api/shares`). */
export const resourceShareKeys = {
  all: ['resource-shares'] as const,
  list: (resourceType: string, resourceId: string) => [...resourceShareKeys.all, resourceType, resourceId] as const,
} as const;

export const appSearchKeys = {
  all: ['app-search'] as const,
  lists: () => [...appSearchKeys.all, 'list'] as const,
  list: (query?: string, kinds?: string) =>
    [...appSearchKeys.lists(), query ?? '', kinds ?? ''] as const,
} as const;

/**
 * FRONT-2391: the command palette's "Recent activity" read (`GET
 * /api/recents`). Same FRONT-1501 reasoning as `appSearchKeys.list` right
 * above — `kinds` gates the apps/sketches/decks sections the same way it
 * gates apps search, so two different kind sets (e.g. Slides off vs on) must
 * never share a cache entry, or a workspace with a flag off would render the
 * OTHER workspace's Recent slides section.
 */
export const recentsKeys = {
  all: ['recents'] as const,
  lists: () => [...recentsKeys.all, 'list'] as const,
  list: (kinds?: string) => [...recentsKeys.lists(), kinds ?? ''] as const,
} as const;

export const sketchKeys = {
  all: ['sketches'] as const,
  /** Every list page regardless of search term — the invalidation target. */
  lists: () => [...sketchKeys.all, 'list'] as const,
  /**
   * One list, keyed BY its search term. The term has to be part of the key:
   * TanStack only refetches when the key changes, so a fixed key leaves the
   * unfiltered first page cached and typing in the search box fires no
   * request at all. That is exactly what FRONT-1501's browser check caught —
   * the box looked wired up and did nothing. Same shape as
   * `graphKeys.entities`, which folds its search term in for the same reason.
   */
  list: (input?: GalleryListKeyInput) => [...sketchKeys.lists(), ...galleryListKeyParts(input)] as const,
} as const;

// FRONT-1986: Eddy Machine — the caller's own byo-daemon machine, Settings →
// Machine. No per-item detail query and no search term to fold in (there is
// at most one machine per person, per the backend's unique index), so
// `list()` is both the read key and the invalidation target every
// create/update/delete mutation invalidates.
export const machineKeys = {
  // The machine probe-status query (use-machines.ts) deliberately lives OUTSIDE this prefix — its own fetch invalidates `all`, so nesting it here would re-probe forever.
  all: ['machines'] as const,
  list: () => [...machineKeys.all, 'list'] as const,
} as const;

// FRONT-3003: Pro Builder's one status read (org switch, caller's admin
// right, the caller's own connection). `status()` nests under `all`, which
// is what both mutations invalidate; there is no per-request input.
export const proBuilderKeys = {
  all: ['pro-builder'] as const,
  status: () => [...proBuilderKeys.all, 'status'] as const,
  // FRONT-3040: the person's one Pro Builder session row, read by the chat
  // top-bar board. No per-request input (one row per person).
  session: () => [...proBuilderKeys.all, 'session'] as const,
  projects: () => [...proBuilderKeys.all, 'projects'] as const,
  // FRONT-3077: the one app the window is bound to. One binding per person, so no per-request input.
  app: () => [...proBuilderKeys.all, 'app'] as const,
  // FRONT-3108: the consent card waiting on the person's one live terminal. One terminal per person, so no per-request input.
  consent: () => [...proBuilderKeys.all, 'consent'] as const,
} as const;

// FRONT-2177: the self-serve pairing flow (Settings → Machine, no machine
// yet) — a short-lived code + install command the person pastes on their
// own server. Same no-per-request-input shape as `machineKeys` above (one
// pairing session per person, and the read always answers "whatever's
// current" with no params), so the only guard worth writing is the one
// every keyed factory in this file shares: `state()` nests under `all`.
export const machinePairingKeys = {
  all: ['machine-pairing'] as const,
  state: () => [...machinePairingKeys.all, 'state'] as const,
} as const;

/**
 * FRONT-1989: the desktop-operator chat card's data (`dispatch_desktop_task`).
 * `run(id)` is the single-run detail read the card polls and the SSE
 * `operator-run-updated` event invalidates. `forToolCall(sessionId,
 * toolCallId)` is the one-time lookup a freshly-mounted card uses to learn
 * its own run id when the tool's output hasn't carried one yet (still
 * running, or the page was reloaded mid-run) — kept under its own
 * `lookups()` prefix, distinct from `runs()`/`run(id)`, so the SSE handler
 * can invalidate "every open card's lookup" without also re-fetching every
 * already-resolved run detail query.
 */
export const operatorKeys = {
  all: ['operator'] as const,
  runs: () => [...operatorKeys.all, 'run'] as const,
  run: (runId: string) => [...operatorKeys.runs(), runId] as const,
  lookups: () => [...operatorKeys.all, 'lookup'] as const,
  forToolCall: (sessionId: string, toolCallId: string) =>
    [...operatorKeys.lookups(), sessionId, toolCallId] as const,
} as const;

// FRONT-2196 "Open desktop": the manual take/release-control lease for
// Settings → Machine's desktop viewer, and the "did a real desktop task
// start instead" read that shares the same endpoint. One lease per
// person's own machine, no per-request input to fold in — same
// no-search-term shape as `machinePairingKeys` above.
export const desktopControlKeys = {
  all: ['desktop-control'] as const,
  state: () => [...desktopControlKeys.all, 'state'] as const,
} as const;

/**
 * FRONT-2392: the Files overview grid — `GET /api/files/overview`, the
 * server-paged, server-searched replacement for the old "fetch the whole
 * library, merge and filter client-side" shape. `tab`, the normalized search
 * term, AND `pageSize` all ride the SPECIFIC key (FRONT-1501 rule: a control
 * missing from the key renders, debounces, and sends no second request) —
 * `pageSize` joined the other two after fix round 1 (F1): the tab-count reads
 * (`pageSize: 1`) and the grid's own read (`pageSize: 50`) used to share one
 * key, so a refetch after invalidation could run with whichever `pageSize`
 * last won the race and the grid would collapse to 1 item. `lists()` is the
 * invalidation prefix every mutation that changes what the grid shows
 * (upload, document save, rename, trash, restore, purge, folder move,
 * smart-tagging, …) invalidates alongside its existing
 * `fileKeys`/`documentKeys`/`folderKeys.membership()` call.
 */
export type FilesOverviewTab = 'open' | 'trash';

/** Normalize a search term ONCE so the same value is used for both the
 *  cache key and the request param — a cache entry can never describe a
 *  different query than the one it holds (same discipline as
 *  `galleryListKeyParts` above). */
export function normalizeOverviewQuery(q?: string): string {
  return q?.trim() ?? '';
}

/** FRONT-2434: the list's sort and filters. Absent fields are each tab's
 *  default (newest change first, no filter), so an untouched list and an
 *  explicit default share one cache entry. */
export interface FilesOverviewView {
  sort?: string;
  dir?: 'asc' | 'desc';
  type?: string | null;
  source?: string | null;
  /** FRONT-2550: `'eddy'` narrows to items Eddy's smart tagging tagged. */
  tagged?: string | null;
}

export function normalizeOverviewView(tab: FilesOverviewTab, view: FilesOverviewView = {}) {
  return {
    sort: view.sort ?? (tab === 'trash' ? 'deleted' : 'changed'),
    dir: view.dir ?? 'desc',
    type: view.type ?? null,
    source: view.source ?? null,
    tagged: view.tagged ?? null,
  } as const;
}

export const filesOverviewKeys = {
  all: ['files-overview'] as const,
  lists: () => [...filesOverviewKeys.all, 'list'] as const,
  list: (input: { tab: FilesOverviewTab; q?: string; pageSize?: number } & FilesOverviewView) => {
    const v = normalizeOverviewView(input.tab, input);
    return [
      ...filesOverviewKeys.lists(),
      input.tab,
      normalizeOverviewQuery(input.q),
      input.pageSize ?? 50,
      v.sort,
      v.dir,
      v.type,
      v.source,
      v.tagged,
    ] as const;
  },
} as const;

/** Whether a previous overview key's rows may stand in while `next` loads:
 *  across a search, sort or filter change — same tab and page size — never a
 *  tab switch. The stand-in rows render dimmed and unselectable. */
export function isSameOverviewView(prev: readonly unknown[] | undefined, next: readonly unknown[]): boolean {
  if (!prev || prev[0] !== next[0] || prev[1] !== next[1]) return false;
  return prev[2] === next[2] && prev[4] === next[4];
}
